// IVF / ART Cost Estimator v0
// Implements the JSON spec provided in the prompt.
//
// ============================================================================
// CURRENT STATUS (v0):
// ============================================================================
// ✅ USED: National-average pricing data (BASE_PRICES, ADD_ONS, MEDS, DX_FEMALE, DX_MALE)
//   - All calculations currently use U.S. national averages from CareCredit 2025
//   - This is what `calculateCost()` returns in production
//
// ❌ UNUSED: City-specific IVF costs (CITY_IVF_COSTS)
//   - Contains pricing for 8 major cities (SF, LA, NYC, NJ, Chicago, Boston, Seattle, Atlanta)
//   - Includes: IVF base, medication, consultation, ICSI, PGT-A, total
//   - NOT currently used in `calculateCost()` - kept for future implementation
//
// ❌ UNUSED: State-specific diagnostic costs (STATE_DIAGNOSTIC_COSTS)
//   - Contains diagnostic test pricing for all 50 U.S. states
//   - Includes: FSH, LH, estradiol, progesterone, AMH, semen analysis, TVUS, HSG
//   - Also includes: insurance mandate status and uninsured rates per state
//   - NOT currently used in `calculateCost()` - kept for future implementation
//
// ============================================================================
// NEXT VERSION (v1) GOALS:
// ============================================================================
// 1. Add `city?: string` and `state?: string` parameters to `CostInput`
// 2. When city is provided, use CITY_IVF_COSTS for base IVF cycle pricing
//    - Fallback to national average if city not found
// 3. When state is provided, use STATE_DIAGNOSTIC_COSTS for diagnostic pricing
//    - Fallback to national average if state not found
// 4. Update UI to allow users to select their city/state for personalized estimates
// 5. Add clear messaging about when estimates are city/state-specific vs national-only
//
// ============================================================================
// Primary data sources (2025):
// ============================================================================
// - CareCredit: IVF pricing overview (cycle, meds, add-ons, etc.)
//   https://www.carecredit.com/well-u/health-wellness/ivf-pricing/
// - FertilityIQ: Cost of IVF by city (used for CITY_IVF_COSTS table)
//   https://www.fertilityiq.com/fertilityiq/articles/the-cost-of-ivf-by-city
// - Scientific article on infertility workup prices by U.S. state (STATE_DIAGNOSTIC_COSTS)
//   https://www.sciencedirect.com/science/article/pii/S2666334124001132

export type TreatmentType =
  | "egg_freezing"
  | "embryo_freezing"
  | "sperm_freezing"
  | "ivf"
  | "ivf_icsi"
  | "fet"
  | "iui"
  | "ovulation_induction";

export type DiagnosticProfile = "female" | "male" | "couple";

export interface CostOptions {
  donor_sperm?: boolean;
  donor_eggs?: boolean;
  pgt?: boolean;
  sperm_freezing?: boolean;
}

export interface CostToggles {
  include_medications?: boolean;
  include_diagnostics?: boolean;
  include_one_year_storage?: boolean;
}

export interface CostInput {
  treatment_type: TreatmentType;
  options?: CostOptions;
  toggles?: CostToggles;
  diagnostic_profile?: DiagnosticProfile;
}

export interface CostBreakdownItem {
  key: string;
  label: string;
  avg: number;
}

export interface CostHeadline {
  total_avg: number;
}

export interface CostResult {
  headline: CostHeadline;
  breakdown: CostBreakdownItem[];
  notes: string[];
}

// ============================================================================
// ❌ UNUSED: City-specific IVF pricing data
// ============================================================================
// This data is available but NOT currently used in `calculateCost()`.
// It contains IVF cycle costs for 8 major U.S. cities from FertilityIQ.
//
// Structure: Each city has IVF base, medication, consultation, ICSI, PGT-A, and total.
// Coverage: SF, LA, NYC, NJ, Chicago, Boston, Seattle, Atlanta (8 cities total).
//
// TODO (v1): Integrate this into `calculateCost()` when `CostInput` includes `city?: string`.
//   - Use city-specific pricing for base IVF cycle when city is provided
//   - Fallback to national average (BASE_PRICES) if city not found or not provided
export const CITY_IVF_COSTS = {
  SF: {
    ivf: 12246,
    medication: 5000,
    consultation: 410,
    icsi: 1891,
    pgta: 5202,
    total: 24749,
  },
  LA: {
    ivf: 13325,
    medication: 5000,
    consultation: 320,
    icsi: 1763,
    pgta: 5475,
    total: 25883,
  },
  NYC: {
    ivf: 9705,
    medication: 5000,
    consultation: 408,
    icsi: 2500,
    pgta: 5881,
    total: 23494,
  },
  NJ: {
    ivf: 10866,
    medication: 5000,
    consultation: 408,
    icsi: 1725,
    pgta: 5000,
    total: 22999,
  },
  Chicago: {
    ivf: 11666,
    medication: 5000,
    consultation: 438,
    icsi: 1750,
    pgta: 5425,
    total: 24279,
  },
  Boston: {
    ivf: 8267,
    medication: 5000,
    consultation: 352,
    icsi: 1280,
    pgta: 5111,
    total: 20010,
  },
  Seattle: {
    ivf: 12450,
    medication: 5000,
    consultation: 312,
    icsi: 900,
    pgta: 4700,
    total: 23362,
  },
  Atlanta: {
    ivf: 11363,
    medication: 5000,
    consultation: 282,
    icsi: 1687,
    pgta: 4800,
    total: 23132,
  },
} as const;

// ============================================================================
// ❌ UNUSED: State-specific diagnostic pricing data
// ============================================================================
// This data is available but NOT currently used in `calculateCost()`.
// It contains diagnostic test costs for all 50 U.S. states from a 2024 ScienceDirect study.
//
// Structure: Each state has individual test costs (FSH, LH, estradiol, progesterone, AMH,
//   semen analysis, TVUS, HSG), total cost, insurance mandate status, and uninsured rate.
// Coverage: All 50 U.S. states + DC (51 total entries).
//
// TODO (v1): Integrate this into `calculateCost()` when `CostInput` includes `state?: string`.
//   - Use state-specific pricing for diagnostics when state is provided
//   - Fallback to national average (DX_FEMALE/DX_MALE) if state not found or not provided
//   - Consider using insurance_mandate and uninsured_rate_pct for more nuanced estimates
export interface StateDiagnosticCosts {
  fsh: number;
  lh: number;
  estradiol: number;
  progesterone: number;
  amh: number;
  semen_analysis: number;
  tvus: number;
  hsg: number;
  total: number;
  insurance_mandate: boolean;
  uninsured_rate_pct: number;
}

export const STATE_DIAGNOSTIC_COSTS: Record<string, StateDiagnosticCosts> = {
  Alabama: {
    fsh: 71.2,
    lh: 100.6,
    estradiol: 106.4,
    progesterone: 93.2,
    amh: 120.4,
    semen_analysis: 105,
    tvus: 140,
    hsg: 417.4,
    total: 1154.2,
    insurance_mandate: false,
    uninsured_rate_pct: 8.8,
  },
  Alaska: {
    fsh: 179.6,
    lh: 214,
    estradiol: 198,
    progesterone: 176.4,
    amh: 310,
    semen_analysis: 322,
    tvus: 364,
    hsg: 1532.6,
    total: 3296.6,
    insurance_mandate: false,
    uninsured_rate_pct: 11,
  },
  Arizona: {
    fsh: 122.6,
    lh: 125.2,
    estradiol: 190.8,
    progesterone: 146.6,
    amh: 216.8,
    semen_analysis: 134.8,
    tvus: 375.8,
    hsg: 531,
    total: 1843.6,
    insurance_mandate: false,
    uninsured_rate_pct: 10.3,
  },
  Arkansas: {
    fsh: 131.6,
    lh: 119.6,
    estradiol: 182.8,
    progesterone: 116.4,
    amh: 170.4,
    semen_analysis: 163.6,
    tvus: 124,
    hsg: 601,
    total: 1609.4,
    insurance_mandate: true,
    uninsured_rate_pct: 8.4,
  },
  California: {
    fsh: 110.6,
    lh: 100.6,
    estradiol: 199.2,
    progesterone: 128.2,
    amh: 131.6,
    semen_analysis: 231.6,
    tvus: 201.4,
    hsg: 724,
    total: 1827.2,
    insurance_mandate: true,
    uninsured_rate_pct: 6.5,
  },
  Colorado: {
    fsh: 164.8,
    lh: 111.2,
    estradiol: 146.6,
    progesterone: 113,
    amh: 204.8,
    semen_analysis: 151.8,
    tvus: 154.6,
    hsg: 1131.6,
    total: 2178.4,
    insurance_mandate: true,
    uninsured_rate_pct: 7.1,
  },
  Connecticut: {
    fsh: 150.4,
    lh: 125,
    estradiol: 214,
    progesterone: 157.2,
    amh: 200.2,
    semen_analysis: 214.6,
    tvus: 230.8,
    hsg: 1316,
    total: 2608.2,
    insurance_mandate: true,
    uninsured_rate_pct: 5.2,
  },
  Delaware: {
    fsh: 94,
    lh: 98,
    estradiol: 150,
    progesterone: 110,
    amh: 131.2,
    semen_analysis: 186.68,
    tvus: 132.28,
    hsg: 179.28,
    total: 1081.4,
    insurance_mandate: true,
    uninsured_rate_pct: 5.6,
  },
  Florida: {
    fsh: 122.4,
    lh: 118.6,
    estradiol: 191.2,
    progesterone: 124.2,
    amh: 175.8,
    semen_analysis: 178.52,
    tvus: 124.96,
    hsg: 189.66,
    total: 1225.3,
    insurance_mandate: false,
    uninsured_rate_pct: 11.2,
  },
  Georgia: {
    fsh: 130.2,
    lh: 122.6,
    estradiol: 190.4,
    progesterone: 146.4,
    amh: 176.2,
    semen_analysis: 180.2,
    tvus: 126.52,
    hsg: 192.64,
    total: 1265.2,
    insurance_mandate: false,
    uninsured_rate_pct: 11.7,
  },
  Hawaii: {
    fsh: 64,
    lh: 64,
    estradiol: 97,
    progesterone: 90,
    amh: 127,
    semen_analysis: 125,
    tvus: 142,
    hsg: 650,
    total: 1359,
    insurance_mandate: true,
    uninsured_rate_pct: 3.5,
  },
  Idaho: {
    fsh: 75.6,
    lh: 95,
    estradiol: 95.2,
    progesterone: 77.6,
    amh: 204.8,
    semen_analysis: 133.2,
    tvus: 180.4,
    hsg: 265.4,
    total: 1127.2,
    insurance_mandate: false,
    uninsured_rate_pct: 8.2,
  },
  Illinois: {
    fsh: 169.2,
    lh: 179.2,
    estradiol: 170.8,
    progesterone: 108.2,
    amh: 200.4,
    semen_analysis: 148,
    tvus: 293.4,
    hsg: 854.2,
    total: 2123.4,
    insurance_mandate: true,
    uninsured_rate_pct: 6.6,
  },
  Indiana: {
    fsh: 115.4,
    lh: 105.4,
    estradiol: 155.6,
    progesterone: 149,
    amh: 139.2,
    semen_analysis: 209,
    tvus: 139.2,
    hsg: 766,
    total: 1778.8,
    insurance_mandate: false,
    uninsured_rate_pct: 7,
  },
  Iowa: {
    fsh: 97.4,
    lh: 120.4,
    estradiol: 131.8,
    progesterone: 84.6,
    amh: 151.2,
    semen_analysis: 129.4,
    tvus: 153.6,
    hsg: 738.6,
    total: 1607,
    insurance_mandate: false,
    uninsured_rate_pct: 4.2,
  },
  Kansas: {
    fsh: 122.2,
    lh: 123.4,
    estradiol: 154.4,
    progesterone: 119,
    amh: 179.6,
    semen_analysis: 148.6,
    tvus: 126.6,
    hsg: 498.6,
    total: 1472.4,
    insurance_mandate: false,
    uninsured_rate_pct: 8.6,
  },
  Kentucky: {
    fsh: 139,
    lh: 95.6,
    estradiol: 133.2,
    progesterone: 110.4,
    amh: 137,
    semen_analysis: 141.56,
    tvus: 107.68,
    hsg: 153.64,
    total: 1018.1,
    insurance_mandate: true,
    uninsured_rate_pct: 5.7,
  },
  Louisiana: {
    fsh: 125.8,
    lh: 124.6,
    estradiol: 192,
    progesterone: 104.8,
    amh: 165,
    semen_analysis: 149.16,
    tvus: 114.24,
    hsg: 155.31,
    total: 1130.9,
    insurance_mandate: true,
    uninsured_rate_pct: 6.9,
  },
  Maine: {
    fsh: 89.8,
    lh: 151.4,
    estradiol: 269,
    progesterone: 147.6,
    amh: 155.2,
    semen_analysis: 161.6,
    tvus: 108.2,
    hsg: 798.6,
    total: 1881.4,
    insurance_mandate: true,
    uninsured_rate_pct: 6.6,
  },
  Maryland: {
    fsh: 92.6,
    lh: 92.2,
    estradiol: 149.4,
    progesterone: 96.4,
    amh: 157.2,
    semen_analysis: 176.08,
    tvus: 113.28,
    hsg: 146.06,
    total: 1023.2,
    insurance_mandate: true,
    uninsured_rate_pct: 6.1,
  },
  Massachusetts: {
    fsh: 117,
    lh: 135,
    estradiol: 185.8,
    progesterone: 139.8,
    amh: 161.8,
    semen_analysis: 206.4,
    tvus: 146.2,
    hsg: 893,
    total: 1985,
    insurance_mandate: true,
    uninsured_rate_pct: 2.4,
  },
  Michigan: {
    fsh: 90.2,
    lh: 91.4,
    estradiol: 128.2,
    progesterone: 94.4,
    amh: 123.2,
    semen_analysis: 106.2,
    tvus: 171.2,
    hsg: 508,
    total: 1312.8,
    insurance_mandate: false,
    uninsured_rate_pct: 4.5,
  },
  Minnesota: {
    fsh: 102.8,
    lh: 130,
    estradiol: 144.4,
    progesterone: 136,
    amh: 187.8,
    semen_analysis: 157.8,
    tvus: 172.4,
    hsg: 1047.8,
    total: 2079,
    insurance_mandate: false,
    uninsured_rate_pct: 4.5,
  },
  Mississippi: {
    fsh: 99.6,
    lh: 98.4,
    estradiol: 128,
    progesterone: 104.4,
    amh: 162.2,
    semen_analysis: 175.36,
    tvus: 122.84,
    hsg: 161.62,
    total: 1052.4,
    insurance_mandate: false,
    uninsured_rate_pct: 10.8,
  },
  Missouri: {
    fsh: 121.8,
    lh: 110.6,
    estradiol: 165.2,
    progesterone: 180.2,
    amh: 192.6,
    semen_analysis: 186.4,
    tvus: 143,
    hsg: 728,
    total: 1827.8,
    insurance_mandate: false,
    uninsured_rate_pct: 8.6,
  },
  Montana: {
    fsh: 197.8,
    lh: 88.2,
    estradiol: 125.2,
    progesterone: 221.4,
    amh: 140.4,
    semen_analysis: 134.2,
    tvus: 185,
    hsg: 417.6,
    total: 1509.8,
    insurance_mandate: true,
    uninsured_rate_pct: 8.3,
  },
  Nebraska: {
    fsh: 154.8,
    lh: 130.2,
    estradiol: 167.6,
    progesterone: 100,
    amh: 120.6,
    semen_analysis: 196.6,
    tvus: 163,
    hsg: 520,
    total: 1552.8,
    insurance_mandate: false,
    uninsured_rate_pct: 6.7,
  },
  Nevada: {
    fsh: 136.2,
    lh: 134.6,
    estradiol: 213.6,
    progesterone: 150.2,
    amh: 177.6,
    semen_analysis: 167.4,
    tvus: 330.8,
    hsg: 620.8,
    total: 1931.2,
    insurance_mandate: false,
    uninsured_rate_pct: 11.1,
  },
  "New Hampshire": {
    fsh: 151.8,
    lh: 148.6,
    estradiol: 177.6,
    progesterone: 165,
    amh: 241.8,
    semen_analysis: 159.92,
    tvus: 151.24,
    hsg: 171.99,
    total: 1368,
    insurance_mandate: true,
    uninsured_rate_pct: 4.9,
  },
  "New Jersey": {
    fsh: 123.8,
    lh: 120.8,
    estradiol: 172.4,
    progesterone: 88.2,
    amh: 182.8,
    semen_analysis: 166.3,
    tvus: 160.61,
    hsg: 171.32,
    total: 1186.2,
    insurance_mandate: true,
    uninsured_rate_pct: 6.8,
  },
  "New Mexico": {
    fsh: 89,
    lh: 108.2,
    estradiol: 125.6,
    progesterone: 109.4,
    amh: 144.4,
    semen_analysis: 161.2,
    tvus: 178.8,
    hsg: 623.6,
    total: 1540.2,
    insurance_mandate: true,
    uninsured_rate_pct: 8.2,
  },
  "New York": {
    fsh: 112.4,
    lh: 130.8,
    estradiol: 158.8,
    progesterone: 116,
    amh: 153.4,
    semen_analysis: 201.6,
    tvus: 235.6,
    hsg: 1060.2,
    total: 2168.8,
    insurance_mandate: true,
    uninsured_rate_pct: 4.9,
  },
  "North Carolina": {
    fsh: 111.6,
    lh: 131.6,
    estradiol: 184.4,
    progesterone: 129,
    amh: 142,
    semen_analysis: 169.6,
    tvus: 125.76,
    hsg: 171.28,
    total: 1165.2,
    insurance_mandate: false,
    uninsured_rate_pct: 9.3,
  },
  "North Dakota": {
    fsh: 120,
    lh: 125.2,
    estradiol: 131,
    progesterone: 117,
    amh: 242.2,
    semen_analysis: 239.6,
    tvus: 131.4,
    hsg: 630.8,
    total: 1737.2,
    insurance_mandate: false,
    uninsured_rate_pct: 6.4,
  },
  Ohio: {
    fsh: 130.4,
    lh: 128.4,
    estradiol: 199.8,
    progesterone: 128,
    amh: 138.8,
    semen_analysis: 177,
    tvus: 180.2,
    hsg: 690.4,
    total: 1773,
    insurance_mandate: true,
    uninsured_rate_pct: 5.9,
  },
  Oklahoma: {
    fsh: 123.8,
    lh: 128,
    estradiol: 149,
    progesterone: 126.8,
    amh: 163.8,
    semen_analysis: 159.92,
    tvus: 119.88,
    hsg: 189.8,
    total: 1161,
    insurance_mandate: false,
    uninsured_rate_pct: 11.7,
  },
  Oregon: {
    fsh: 71.6,
    lh: 118.6,
    estradiol: 107.8,
    progesterone: 78.8,
    amh: 132,
    semen_analysis: 160.14,
    tvus: 116.34,
    hsg: 182.17,
    total: 967.5,
    insurance_mandate: false,
    uninsured_rate_pct: 6,
  },
  Pennsylvania: {
    fsh: 106.2,
    lh: 126.2,
    estradiol: 160.6,
    progesterone: 108.2,
    amh: 185.6,
    semen_analysis: 154.4,
    tvus: 115.92,
    hsg: 181.25,
    total: 1138.4,
    insurance_mandate: false,
    uninsured_rate_pct: 5.3,
  },
  "Rhode Island": {
    fsh: 85.2,
    lh: 126.2,
    estradiol: 142,
    progesterone: 107.2,
    amh: 156.8,
    semen_analysis: 149.64,
    tvus: 111.76,
    hsg: 178.21,
    total: 1057,
    insurance_mandate: true,
    uninsured_rate_pct: 4.2,
  },
  "South Carolina": {
    fsh: 98.4,
    lh: 102.6,
    estradiol: 157,
    progesterone: 120.6,
    amh: 86.4,
    semen_analysis: 148.36,
    tvus: 111,
    hsg: 173.45,
    total: 997.8,
    insurance_mandate: false,
    uninsured_rate_pct: 9.1,
  },
  "South Dakota": {
    fsh: 127,
    lh: 127,
    estradiol: 179.4,
    progesterone: 700,
    amh: 185.6,
    semen_analysis: 187.4,
    tvus: 158,
    hsg: 491,
    total: 2155.4,
    insurance_mandate: false,
    uninsured_rate_pct: 8.1,
  },
  Tennessee: {
    fsh: 103.6,
    lh: 104.2,
    estradiol: 138,
    progesterone: 115.6,
    amh: 135.4,
    semen_analysis: 149.3,
    tvus: 206.93,
    hsg: 159.98,
    total: 1113,
    insurance_mandate: false,
    uninsured_rate_pct: 9.3,
  },
  Texas: {
    fsh: 113.8,
    lh: 122.2,
    estradiol: 156.8,
    progesterone: 125.2,
    amh: 167.2,
    semen_analysis: 194,
    tvus: 172.4,
    hsg: 826.2,
    total: 1877.8,
    insurance_mandate: true,
    uninsured_rate_pct: 16.6,
  },
  Utah: {
    fsh: 66,
    lh: 85.6,
    estradiol: 73.8,
    progesterone: 73.4,
    amh: 104.6,
    semen_analysis: 136.8,
    tvus: 118,
    hsg: 351,
    total: 1009.2,
    insurance_mandate: true,
    uninsured_rate_pct: 8.1,
  },
  Vermont: {
    fsh: 109,
    lh: 129.4,
    estradiol: 179.6,
    progesterone: 145,
    amh: 179.2,
    semen_analysis: 141,
    tvus: 226.96,
    hsg: 163.17,
    total: 1273.3,
    insurance_mandate: false,
    uninsured_rate_pct: 3.9,
  },
  Virginia: {
    fsh: 58.6,
    lh: 70,
    estradiol: 82.8,
    progesterone: 137.2,
    amh: 121,
    semen_analysis: 145.52,
    tvus: 231.84,
    hsg: 161.7,
    total: 1008.7,
    insurance_mandate: false,
    uninsured_rate_pct: 6.5,
  },
  Washington: {
    fsh: 99.8,
    lh: 98.2,
    estradiol: 151.8,
    progesterone: 132.8,
    amh: 142,
    semen_analysis: 132,
    tvus: 190.2,
    hsg: 567.2,
    total: 1514,
    insurance_mandate: true,
    uninsured_rate_pct: 6.1,
  },
  "West Virginia": {
    fsh: 134.6,
    lh: 128.6,
    estradiol: 206.6,
    progesterone: 132.4,
    amh: 167.8,
    semen_analysis: 146.6,
    tvus: 204.2,
    hsg: 155.1,
    total: 1266.8,
    insurance_mandate: true,
    uninsured_rate_pct: 5.9,
  },
  Wisconsin: {
    fsh: 151.2,
    lh: 146.2,
    estradiol: 183.8,
    progesterone: 165.8,
    amh: 210.8,
    semen_analysis: 198.8,
    tvus: 308,
    hsg: 1101.8,
    total: 2466.4,
    insurance_mandate: false,
    uninsured_rate_pct: 5.2,
  },
  Wyoming: {
    fsh: 130,
    lh: 227,
    estradiol: 197,
    progesterone: 142,
    amh: 205,
    semen_analysis: 204,
    tvus: 237,
    hsg: 814,
    total: 2156,
    insurance_mandate: false,
    uninsured_rate_pct: 11.5,
  },
};

// ============================================================================
// ✅ USED: National-average price catalog (currently active in production)
// ============================================================================
// These constants are actively used by `calculateCost()` to generate estimates.
// All prices are U.S. national averages from CareCredit 2025 data.
//
// BASE_PRICES: Base treatment cycle costs (egg freezing, IVF, FET, IUI, etc.)
// ADD_ONS: Optional add-on services (ICSI, PGT-A, donor services, storage, etc.)
// MEDS: Medication costs per treatment type
// DX_FEMALE: Female diagnostic test costs (hormone panel, exam, ultrasound)
// DX_MALE: Male diagnostic test costs (exam, semen analysis)
//
// Note: These are national averages. For city/state-specific pricing, see
// CITY_IVF_COSTS and STATE_DIAGNOSTIC_COSTS above (currently unused).

const BASE_PRICES: Record<TreatmentType, { label: string; avg: number }> = {
  egg_freezing: {
    label: "Egg freezing cycle (monitoring, retrieval, lab, initial freezing)",
    avg: 8800,
  },
  embryo_freezing: {
    label:
      "Embryo freezing cycle (stimulation, retrieval, fertilization, freezing)",
    avg: 10500,
  },
  sperm_freezing: {
    label: "Sperm freezing (collection, analysis, initial cryopreservation)",
    avg: 1000,
  },
  ivf: {
    label: "IVF cycle (without ICSI or PGT-A)",
    avg: 11200,
  },
  ivf_icsi: {
    label: "IVF cycle (ICSI added as separate line)",
    avg: 11200,
  },
  fet: {
    label: "Frozen embryo transfer (FET)",
    avg: 4500,
  },
  iui: {
    label: "IUI (procedure only)",
    avg: 2000,
  },
  ovulation_induction: {
    label: "Ovulation induction (monitoring)",
    avg: 1600,
  },
};

const ADD_ONS = {
  anesthesia: {
    key: "anesthesia",
    label: "Anesthesia (IVF/egg retrieval)",
    avg: 655,
  },
  assisted_hatching: {
    key: "assisted_hatching",
    label: "Assisted hatching",
    avg: 652,
  },
  icsi: {
    key: "icsi",
    label: "Intracytoplasmic sperm injection (ICSI)",
    avg: 1027,
  },
  // PGT-A costs by city (FertilityIQ data):
  // SF: $5,202 | LA: $5,475 | NYC: $5,881 | NJ: $5,000
  // Chicago: $5,425 | Boston: $5,111 | Seattle: $4,700 | Atlanta: $4,800
  // Mean: $5,199 ≈ $5,200
  pgt_testing_block: {
    key: "pgt",
    label: "PGT-A genetic testing (per embryo batch)",
    avg: 5200,
  },
  embryo_freeze_initial: {
    key: "embryo_freeze_initial",
    label: "Embryo cryopreservation (initial freezing)",
    avg: 4125,
  },
  annual_storage: {
    key: "storage",
    label: "Egg/embryo/sperm storage (1 year)",
    avg: 730,
  },
  donor_sperm_bundle: {
    key: "donor_sperm",
    label: "Donor sperm (per vial)",
    avg: 836,
  },
  // Donor egg costs (eggs only - IVF cycle cost added separately)
  // Sources:
  // - Egg bank: $18,900 (standard frozen batch 6-8 eggs), $22,900 (premium)
  // - Range across providers: $11,000 - $60,000+
  // - Note: Full donor egg IVF cycle median ~$38,000 (includes IVF procedure)
  donor_eggs_fresh_cycle: {
    key: "donor_eggs_fresh_cycle",
    label: "Donor eggs (fresh cycle)",
    avg: 25000, // Fresh cycles typically cost more due to coordination
  },
  donor_eggs_frozen_cycle: {
    key: "donor_eggs_frozen_cycle",
    label: "Donor eggs (frozen batch, 6-8 eggs)",
    avg: 20900, // Mean of $18,900 standard + $22,900 premium
  },
  donor_embryo_cycle: {
    key: "donor_embryo_cycle",
    label: "Donor embryo (per cycle)",
    avg: 2917,
  },
  sperm_freezing_initial: {
    key: "sperm_freezing_initial",
    label: "Sperm cryopreservation (initial freezing)",
    avg: 452,
  },
  gestational_carrier: {
    key: "gestational_carrier",
    label: "Gestational carrier",
    avg: 51232,
  },
} as const;

const MEDS: Record<
  Exclude<TreatmentType, "ivf_icsi" | "sperm_freezing">,
  { label: string; avg: number }
> = {
  egg_freezing: {
    label: "Injectable hormone medications (egg freezing)",
    avg: 3991,
  },
  embryo_freezing: {
    label: "Injectable hormone medications (embryo freezing)",
    avg: 3991,
  },
  ivf: {
    label: "Injectable hormone medications (IVF/IVF+ICSI)",
    avg: 3991,
  },
  fet: {
    label: "Medications for FET preparation",
    avg: 750,
  },
  iui: {
    label: "Medications for IUI",
    avg: 775,
  },
  ovulation_induction: {
    label: "Medications for ovulation induction",
    avg: 152,
  },
};

const DX_FEMALE = {
  hormone_panel: {
    key: "dx_female_hormones",
    label: "Female hormone panel (FSH, LH, estradiol, progesterone, AMH)",
    avg: 400,
  },
  physical_exam: {
    key: "dx_female_exam",
    label: "Physical exam and consultation",
    avg: 200,
  },
  ultrasound: {
    key: "dx_female_ultrasound",
    label: "Transvaginal ultrasound (TVUS)",
    avg: 234,
  },
} as const;

const DX_MALE = {
  physical_exam: {
    key: "dx_male_exam",
    label: "Physical exam and consultation",
    avg: 200,
  },
  semen_analysis: {
    key: "dx_male_semen",
    label: "Semen analysis",
    avg: 50,
  },
} as const;

const DEFAULT_NOTES: string[] = [
  "Estimate is for one treatment cycle.",
  "Female diagnostics include: hormone panel, physical exam, and pelvic/transvaginal ultrasound.",
  "Male diagnostics (if selected) include: physical exam and semen analysis.",
  "Storage costs are for 1 year; long-term storage is a recurring annual fee.",
  "National-average prices are derived from CareCredit's 2025 IVF pricing overview and related cost tables.",
  "City-level IVF pricing is based on FertilityIQ's 'Cost of IVF by City' data.",
  "State-level diagnostic prices come from a 2024 ScienceDirect article on infertility workup costs by state.",
];

function addLine(
  lines: CostBreakdownItem[],
  item: { key: string; label: string; avg: number },
) {
  lines.push({
    key: item.key,
    label: item.label,
    avg: item.avg,
  });
}

// ============================================================================
// ✅ MAIN CALCULATION FUNCTION (currently in production)
// ============================================================================
// This function generates cost estimates using U.S. national averages only.
//
// USES:
//   - BASE_PRICES: Base treatment cycle costs
//   - ADD_ONS: Optional add-on services (ICSI, PGT-A, donor services, etc.)
//   - MEDS: Medication costs per treatment type
//   - DX_FEMALE / DX_MALE: Diagnostic test costs (national averages)
//
// DOES NOT USE (yet):
//   - CITY_IVF_COSTS: City-specific IVF pricing (not integrated)
//   - STATE_DIAGNOSTIC_COSTS: State-specific diagnostic pricing (not integrated)
//
// TODO (v1): Add `city?: string` and `state?: string` to `CostInput` and use
//   CITY_IVF_COSTS and STATE_DIAGNOSTIC_COSTS when provided.
export function calculateCost(input: CostInput): CostResult {
  const {
    treatment_type,
    options = {},
    toggles = {},
    diagnostic_profile = "female",
  } = input;

  const breakdown: CostBreakdownItem[] = [];

  // 1) Base
  const base = BASE_PRICES[treatment_type];
  addLine(breakdown, { key: "base", label: base.label, avg: base.avg });

  // 2) ivf_icsi always add ICSI
  if (treatment_type === "ivf_icsi") {
    addLine(breakdown, ADD_ONS.icsi);
  }

  // 3) PGT
  if (
    options.pgt &&
    (treatment_type === "ivf" ||
      treatment_type === "ivf_icsi" ||
      treatment_type === "embryo_freezing")
  ) {
    addLine(breakdown, ADD_ONS.pgt_testing_block);
  }

  // 4) Donor sperm
  if (options.donor_sperm) {
    addLine(breakdown, ADD_ONS.donor_sperm_bundle);
  }

  // 5) Donor eggs (simplified: use frozen cohort version)
  if (
    options.donor_eggs &&
    (treatment_type === "ivf" ||
      treatment_type === "ivf_icsi" ||
      treatment_type === "embryo_freezing")
  ) {
    addLine(breakdown, ADD_ONS.donor_eggs_frozen_cycle);
  }

  // 6) Sperm freezing (+ optional storage)
  const includeStorage = toggles.include_one_year_storage ?? true;
  if (options.sperm_freezing) {
    addLine(breakdown, ADD_ONS.sperm_freezing_initial);
    if (includeStorage) {
      addLine(breakdown, ADD_ONS.annual_storage);
    }
  }

  // 7) Embryo freezing for IVF / IVF+ICSI / FET / embryo_freezing
  if (
    treatment_type === "ivf" ||
    treatment_type === "ivf_icsi" ||
    treatment_type === "fet" ||
    treatment_type === "embryo_freezing"
  ) {
    addLine(breakdown, ADD_ONS.embryo_freeze_initial);
    if (includeStorage) {
      addLine(breakdown, ADD_ONS.annual_storage);
    }
  }

  // 7b) Standalone sperm freezing: add storage if requested (initial freeze is in base)
  if (treatment_type === "sperm_freezing" && includeStorage) {
    addLine(breakdown, ADD_ONS.annual_storage);
  }

  // 8) Medications
  if (toggles.include_medications && treatment_type !== "sperm_freezing") {
    if (treatment_type === "ivf_icsi") {
      const medsIvf = MEDS.ivf;
      addLine(breakdown, {
        key: "meds",
        label: medsIvf.label,
        avg: medsIvf.avg,
      });
    } else {
      const meds =
        MEDS[
          treatment_type as Exclude<
            TreatmentType,
            "ivf_icsi" | "sperm_freezing"
          >
        ];
      if (meds) {
        addLine(breakdown, {
          key: "meds",
          label: meds.label,
          avg: meds.avg,
        });
      }
    }
  }

  // 9) Diagnostics
  if (toggles.include_diagnostics) {
    if (diagnostic_profile === "female" || diagnostic_profile === "couple") {
      addLine(breakdown, DX_FEMALE.hormone_panel);
      addLine(breakdown, DX_FEMALE.physical_exam);
      addLine(breakdown, DX_FEMALE.ultrasound);
    }
    if (diagnostic_profile === "male" || diagnostic_profile === "couple") {
      addLine(breakdown, DX_MALE.physical_exam);
      addLine(breakdown, DX_MALE.semen_analysis);
    }
  }

  // 10) Sum total
  const total_avg = breakdown.reduce((sum, item) => sum + item.avg, 0);

  const headline: CostHeadline = {
    total_avg,
  };

  return {
    headline,
    breakdown,
    notes: DEFAULT_NOTES,
  };
}
